/**
 * Factoring Data Fetching
 *
 * Functions to fetch factoring data directly from the backend API.
 * Includes caching to avoid duplicate requests for the same MC number.
 * Extended with Compass, TAFS, Engaged Financial endpoints (match Chrome extension).
 */

import axios from 'axios'
import { FactoringProvider, FactoringDataResponse, ProviderResponseMap } from './types'

export type FactoringLookupError = 'auth' | 'not_found' | 'unavailable'

/** Map transport failures to factoring lookup states without treating an upstream outage as an unrated broker. */
export function classifyFactoringLookupError(error: unknown): FactoringLookupError {
    if (!axios.isAxiosError(error)) return 'unavailable'
    const status = error.response?.status
    if (status === 401 || status === 403) return 'auth'
    if (status === 404) return 'not_found'
    return 'unavailable'
}

const API_ENDPOINTS: Record<FactoringProvider, string> = {
    rts: '/integrations/rts/rating',
    triumph: '/triumph/credit-check',
    // Triumph SFTP is submission-only; ratings still flow through the Triumph OAuth API.
    triumph_sftp: '/triumph/credit-check',
    wex: '/factoring/wex/rating',
    winfactor: '/factoring/winfactor/rating',
    gsquared: '/factoring/gsquared/rating',
    capital_depot: '/factoring/capital-depot/rating',
    compass: '/factoring/compass/rating',
    tafs: '/factoring/tafs/rating',
    stratopay: '/factoring/stratopay/rating',
    engaged_financial: '/factoring/engaged-financial/rating',
    haul_pay: '/factoring/haul-pay/rating',
    otr_solutions: '/factoring/otr-solutions/rating',
    flexent: '/factoring/flexent/rating',
    basicblock: '/factoring/basicblock/rating',
    // Outgo has no rating endpoint in Phase 1
    outgo: '',
    // Artispro is submit-only — no rating endpoint
    artispro: '',
    ecapital: '/factoring/ecapital/rating',
    // England is a read-only status source — no rating endpoint
    england: '',
}

const COMPANY_LOOKUP_API_ENDPOINTS: Partial<Record<FactoringProvider, string>> = {
    [FactoringProvider.RTS]: '/integrations/rts/rating/by-company',
    [FactoringProvider.TAFS]: '/factoring/tafs/rating/by-company',
    [FactoringProvider.ECAPITAL]: '/factoring/ecapital/rating/by-company',
}

// Cache for factoring data by provider and lookup key (MC or company name)
type FactoringDataCache = {
    data: FactoringDataResponse | null
    timestamp: number
}

const dataCache = new Map<string, FactoringDataCache>()
const pendingRequests = new Map<string, Promise<FactoringDataResponse | null>>()
const DATA_CACHE_TTL = 60000 // 60 seconds

function getCacheKey(provider: FactoringProvider, mcNumber: string): string {
    return `${provider}:${mcNumber}`
}

async function fetchWithCache(cacheKey: string, request: () => Promise<FactoringDataResponse | null>): Promise<FactoringDataResponse | null> {
    const now = Date.now()
    const cached = dataCache.get(cacheKey)
    if (cached && now - cached.timestamp < DATA_CACHE_TTL) return cached.data

    const pending = pendingRequests.get(cacheKey)
    if (pending) return pending

    const requestPromise = request()
        .then((result) => {
            dataCache.set(cacheKey, { data: result, timestamp: now })
            return result
        })
        .finally(() => {
            pendingRequests.delete(cacheKey)
        })
    pendingRequests.set(cacheKey, requestPromise)
    return requestPromise
}

/**
 * Clear the factoring data cache
 */
export function clearFactoringDataCache(): void {
    dataCache.clear()
    pendingRequests.clear()
}

/**
 * Fetch factoring data directly from backend API
 * Returns the correct response type based on the provider
 * Uses caching to avoid duplicate requests for the same MC number
 */
export async function fetchFactoringData<T extends FactoringProvider>(provider: T, mcNumber: string): Promise<ProviderResponseMap[T] | null>
export async function fetchFactoringData(provider: FactoringProvider, mcNumber: string): Promise<FactoringDataResponse | null> {
    if (!mcNumber) return null

    // Providers with no broker-rating endpoint (Outgo = per-load factorable flag; Artispro = submit-only)
    // have an empty API path. Interpolating it would send a malformed `/v1/<mc>` request (no provider
    // segment) that 404s. Bail out here so no caller can leak that request, regardless of its own guards.
    if (!API_ENDPOINTS[provider]) return null

    return fetchWithCache(getCacheKey(provider, mcNumber), async () => {
        const endpoint = `${API_ENDPOINTS[provider]}/${mcNumber}`
        const response = await axios.get(endpoint)

        // Engaged Financial returns data directly (no {success, data} wrapper) per extension analysis
        if (provider === FactoringProvider.ENGAGED_FINANCIAL && response.data && !response.data.success) {
            return { success: true, data: response.data } as ProviderResponseMap[typeof provider]
        }
        if (response.data?.success) return { success: true, data: response.data.data } as ProviderResponseMap[typeof provider]
        return null
    })
}

/**
 * Fetch a rating by broker COMPANY NAME for providers whose directory supports it. Used by Sylectus
 * box-truck loads that carry no MC. Responses share the normal provider types and the same 60-second
 * cache/in-flight dedupe as MC lookups; the provider is part of the key so RTS and TAFS never collide.
 */
export async function fetchFactoringDataByCompany<T extends FactoringProvider>(provider: T, companyName: string): Promise<ProviderResponseMap[T] | null>
export async function fetchFactoringDataByCompany(provider: FactoringProvider, companyName: string): Promise<FactoringDataResponse | null> {
    const name = companyName?.trim()
    if (!name) return null
    const endpoint = COMPANY_LOOKUP_API_ENDPOINTS[provider]
    if (!endpoint) return null

    return fetchWithCache(`${provider}-company:${name.toLowerCase()}`, async () => {
        try {
            const response = await axios.get(endpoint, { params: { name } })
            return response.data?.success && response.data.data ? ({ success: true, data: response.data.data } as FactoringDataResponse) : null
        } catch (error) {
            // A genuine 404 is stable broker-specific absence, so cache it as null for the normal TTL.
            // Auth and upstream failures must keep rejecting so the UI can show Connect/Unavailable.
            if (axios.isAxiosError(error) && error.response?.status === 404) return null
            throw error
        }
    })
}
